import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from "sonner";
import { supabaseBikeService } from '@/api/supabaseBikeService';
import { userService } from '@/api/userService';
import { supabase } from '@/integrations/supabase/client';
import { Bike, BikeStatus } from '@/api/bikeService';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Card, CardContent } from '@/components/ui/card';
import { Loader2 } from 'lucide-react';
import { UserDetails } from '@/api/users/types';

// Define props for EditBikeForm component
interface EditBikeFormProps {
  bike: string; // We accept bike ID as a string
}

// Define the form schema validation
const formSchema = z.object({
  serialNumber: z.string().min(1, 'Serial number is required'),
  bikeStatus: z.string().min(1, 'Status is required'),
  batteryStatus: z.coerce.number().min(0).max(100).optional(),
  totalMileage: z.coerce.number().min(0).optional(),
  locationType: z.string().min(1, 'Location type is required'),
  locationValue: z.string().optional(),
  modelId: z.string().optional(),
});

type FormValues = z.infer<typeof formSchema>;

const EditBikeForm: React.FC<EditBikeFormProps> = ({ bike: bikeId }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [bikeData, setBikeData] = useState<Bike | null>(null);
  const [sites, setSites] = useState<any[]>([]);
  const [users, setUsers] = useState<UserDetails[]>([]);
  const [bikeModels, setBikeModels] = useState<any[]>([]);
  const [locationType, setLocationType] = useState<'site' | 'home'>('site');
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  // Set up form with default values
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      serialNumber: '',
      bikeStatus: 'active',
      batteryStatus: 0,
      totalMileage: 0,
      locationType: 'site',
      locationValue: '',
      modelId: '',
    },
  });

  // Fetch bike details, sites, and users when component mounts
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        // Fetch bike details using the bikeId
        const bikeDetails = await supabaseBikeService.getBike(bikeId);
        console.log('Fetched bike details:', bikeDetails);
        setBikeData(bikeDetails);

        // Fetch all sites
        const { data: sitesData } = await supabase.from('sites').select('*');
        setSites(sitesData || []);

        // Fetch all users
        const { users: usersData } = await userService.getUsers();
        setUsers(usersData || []);

        // Fetch bike models
        const { data: modelsData } = await supabase.from('bike_models').select('*');
        setBikeModels(modelsData || []);

        // Determine if bike is at a site or user's home
        let locationTypeValue: 'site' | 'home' = 'site';
        let locationValue = '';

        if (bikeDetails.userLocationId) {
          locationTypeValue = 'home';
          locationValue = typeof bikeDetails.userLocationId === 'boolean' ? '' : bikeDetails.userLocationId;
        } else if (bikeDetails.assignedLocation) {
          // Find the site ID that matches the assigned location name
          const site = sitesData?.find(s => s.name === bikeDetails.assignedLocation);
          if (site) {
            locationValue = site.id;
          }
        }

        setLocationType(locationTypeValue);

        // Set form values based on bike data
        form.reset({
          serialNumber: bikeDetails.serialNumber || '',
          bikeStatus: bikeDetails.bikeStatus || 'active',
          batteryStatus: bikeDetails.batteryStatus || 0,
          totalMileage: bikeDetails.totalMileage || 0,
          locationType: locationTypeValue,
          locationValue: locationValue,
          modelId: bikeDetails.modelId || '',
        });
        
        console.log('Form reset with values:', {
          serialNumber: bikeDetails.serialNumber,
          bikeStatus: bikeDetails.bikeStatus,
          batteryStatus: bikeDetails.batteryStatus,
          totalMileage: bikeDetails.totalMileage,
          locationType: locationTypeValue,
          locationValue: locationValue,
          modelId: bikeDetails.modelId,
        });
      } catch (error) {
        console.error('Error fetching data:', error);
        setError('Failed to load bike data. The bike may not exist or you may not have permission to view it.');
        toast.error('Failed to load bike data');
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [bikeId, form]);

  // Handle form submission
  const onSubmit = async (values: FormValues) => {
    setIsSaving(true);

    try {
      const updateData: Partial<Bike> = {
        serialNumber: values.serialNumber,
        bikeStatus: values.bikeStatus as BikeStatus,
        batteryStatus: values.batteryStatus,
        totalMileage: values.totalMileage,
      };

      // Handle location based on locationType
      if (values.locationType === 'site' && values.locationValue) {
        // Find site name based on site ID
        const site = sites.find(s => s.id === values.locationValue);
        updateData.assignedLocation = site ? site.name : null;
        updateData.userLocationId = null; // Ensure user_location_id is null for site locations
      } else if (values.locationType === 'home' && values.locationValue) {
        // Set assigned location to null since it's at a user's home
        updateData.assignedLocation = null;
        updateData.userLocationId = values.locationValue;
      }

      // Update bike details with supabaseBikeService
      await supabaseBikeService.updateBike(bikeId, updateData);

      toast.success('Bike updated successfully');
      navigate(`/bikes/${bikeId}`);
    } catch (error) {
      console.error('Error updating bike:', error);
      toast.error('Failed to update bike');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 border border-destructive/50 rounded-lg bg-destructive/10 text-destructive">
        <h3 className="text-lg font-semibold mb-2">Error</h3>
        <p>{error}</p>
        <Button variant="outline" className="mt-4" onClick={() => navigate('/bikes')}>
          Return to Bikes List
        </Button>
      </div>
    );
  }

  return (
    <Card>
      <CardContent className="pt-6">
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <FormField
                control={form.control}
                name="serialNumber"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Serial Number</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter serial number" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="bikeStatus"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Status</FormLabel>
                    <Select
                      value={field.value}
                      onValueChange={field.onChange}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select status" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="active">Active</SelectItem>
                        <SelectItem value="inactive">Inactive</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="batteryStatus"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Battery Level (%)</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        min="0"
                        max="100"
                        placeholder="0-100%"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="totalMileage"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Total Mileage (km)</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        min="0"
                        step="any"
                        placeholder="Enter total mileage"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="locationType"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Location Type</FormLabel>
                    <Select
                      value={field.value}
                      onValueChange={(value) => {
                        field.onChange(value);
                        setLocationType(value as 'site' | 'home');
                        form.setValue('locationValue', ''); // Reset location value
                      }}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select location type" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="site">Site</SelectItem>
                        <SelectItem value="home">Home</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="locationValue"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{locationType === 'site' ? 'Site' : 'User'}</FormLabel>
                    <Select
                      value={field.value}
                      onValueChange={field.onChange}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder={`Select ${locationType === 'site' ? 'site' : 'user'}`} />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {locationType === 'site' ? (
                          sites.map((site) => (
                            <SelectItem key={site.id} value={site.id}>
                              {site.name}
                            </SelectItem>
                          ))
                        ) : (
                          users.map((user) => (
                            <SelectItem key={user.id} value={user.id}>
                              {user.name}
                            </SelectItem>
                          ))
                        )}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="modelId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Bike Model</FormLabel>
                    <Select
                      value={field.value}
                      onValueChange={field.onChange}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select bike model" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {bikeModels.map((model) => (
                          <SelectItem key={model.id} value={model.id}>
                            {model.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="flex justify-end space-x-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate(`/bikes/${bikeId}`)}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isSaving}>
                {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Save Changes
              </Button>
            </div>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
};

export default EditBikeForm;
